import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  prevStep,
  setStep,
  addJointApplicationData,
} from "app/lib/store/slices/steps/loanTypesSteps";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InputField from "app/components/common/inputs/InputField";
import FormGroup from "app/components/common/inputs/FormGroup";
import SubTitle from "app/components/common/typography/SubTitle";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { LiabilitiesForm } from "app/utils/applicant/types/types";

interface FormData {
  yourDetails: LiabilitiesForm;
  coBorrowerDetails: LiabilitiesForm;
}

const Liabilities: React.FC = () => {
  const dispatch = useDispatch();
  const applicationData = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const { vehicleDetail } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const isRefinance = vehicleDetail?.purchaseFrom === "Refinance";

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      yourDetails: {
        creditCardLimits: 0,
        sumOfAllOtherLoans: 0,
        totalMonthlyRepaymentsOnOtherLoans: 0,
      },
      coBorrowerDetails: {
        creditCardLimits: 0,
        sumOfAllOtherLoans: 0,
        totalMonthlyRepaymentsOnOtherLoans: 0,
      },
    },
  });

  const onSubmit = (data: FormData) => {
    const { coBorrowerDetails, yourDetails } = data;

    dispatch(
      addJointApplicationData({
        category: "YourDetails",
        data: {
          ...(applicationData?.YourDetails || {}),
          liabilities: {
            ...applicationData?.YourDetails?.liabilities,
            creditCardLimits: Number(yourDetails.creditCardLimits) || 0,
            sumOfAllOtherLoans: Number(yourDetails.sumOfAllOtherLoans) || 0,
            totalMonthlyRepaymentsOnOtherLoans:
              Number(yourDetails.totalMonthlyRepaymentsOnOtherLoans) || 0,
          },
        },
      })
    );
    dispatch(
      addJointApplicationData({
        category: "Coborrower",
        data: {
          ...(applicationData?.Coborrower || {}),
          liabilities: {
            ...applicationData?.Coborrower?.liabilities,
            creditCardLimits: Number(coBorrowerDetails.creditCardLimits) || 0,
            sumOfAllOtherLoans:
              Number(coBorrowerDetails.sumOfAllOtherLoans) || 0,
            totalMonthlyRepaymentsOnOtherLoans:
              Number(coBorrowerDetails.totalMonthlyRepaymentsOnOtherLoans) || 0,
          },
        },
      })
    );
    dispatch(setStep(isRefinance ? 13 : 12));
  };

  useEffect(() => {
    if (
      applicationData?.YourDetails?.liabilities ||
      applicationData?.Coborrower?.liabilities ||
      applicationData?.YourDetails?.expenses ||
      applicationData?.Coborrower?.expenses
    ) {
      reset({
        yourDetails: {
          creditCardLimits:
            applicationData?.YourDetails?.liabilities?.creditCardLimits,
          sumOfAllOtherLoans:
            applicationData?.YourDetails?.liabilities?.sumOfAllOtherLoans,
          totalMonthlyRepaymentsOnOtherLoans:
            applicationData?.YourDetails?.liabilities
              ?.totalMonthlyRepaymentsOnOtherLoans,
        },
        coBorrowerDetails: {
          creditCardLimits:
            applicationData?.Coborrower?.liabilities?.creditCardLimits,
          sumOfAllOtherLoans:
            applicationData?.Coborrower?.liabilities?.sumOfAllOtherLoans,
          totalMonthlyRepaymentsOnOtherLoans:
            applicationData?.Coborrower?.liabilities
              ?.totalMonthlyRepaymentsOnOtherLoans,
        },
      });
    }
  }, [applicationData, reset]);

  return (
    <>
      <ProgressBar progress={100} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Liabilities" />
        <SubTitle title="Please outline your total credit card limit and all other personal/property loans" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <FormGroup>
                <InputField
                  label="Total credit card limits"
                  name="yourDetails.creditCardLimits"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.yourDetails?.creditCardLimits?.message}
                  className="flex-1"
                  rules={{
                    // required: "Field is required",
                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Combined limits of all borrowers, including store cards."
                />
              </FormGroup>
              <FormGroup className="sm:items-end">
                <InputField
                  label="Sum of all other loans"
                  name="yourDetails.sumOfAllOtherLoans"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.yourDetails?.sumOfAllOtherLoans?.message}
                  className="flex-1 "
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Include all loan amounts for car loans, personal loans, or student loans."
                />
                <InputField
                  label="Total monthly repayments on other loans"
                  name="yourDetails.totalMonthlyRepaymentsOnOtherLoans"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={
                    errors.yourDetails?.totalMonthlyRepaymentsOnOtherLoans
                      ?.message
                  }
                  className="flex-1 sm:mt-0 mt-5"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Include all borrowers car loans, personal loans or student loan repayments."
                />
              </FormGroup>
            </div>
            <div className="flex-1 sm:mt-0 mt-5">
              <InfoTitle title="Co-borrower" />

              <FormGroup>
                <InputField
                  label="Total credit card limits"
                  name="coBorrowerDetails.creditCardLimits"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.coBorrowerDetails?.creditCardLimits?.message}
                  className="flex-1"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Combined limits of all borrowers, including store cards."
                />
              </FormGroup>
              <FormGroup className="sm:items-end">
                <InputField
                  label="Sum of all other loans"
                  name="coBorrowerDetails.sumOfAllOtherLoans"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.coBorrowerDetails?.sumOfAllOtherLoans?.message}
                  className="flex-1"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Include all loan amounts for car loans, personal loans, or student loans."
                />
                <InputField
                  label="Total monthly repayments on other loans"
                  name="coBorrowerDetails.totalMonthlyRepaymentsOnOtherLoans"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={
                    errors.coBorrowerDetails?.totalMonthlyRepaymentsOnOtherLoans
                      ?.message
                  }
                  className="flex-1 sm:mt-0 mt-5"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Include all borrowers car loans, personal loans or student loan repayments."
                />
              </FormGroup>
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit">Next</ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default Liabilities;
